import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import PaymentError from "../paymentError";
import CustomerDetailsForm from "./customerDetailsForm";
import type { CustomerDetails } from "./customerDetailsForm";
import PaymentMethod from "./paymentMethod";
import PaymentConfirmationLayout from "../confirmation/PaymentConfirmationLayout";
import Info from "../info";
import * as rpc from "../../../common/jsonRpcRoutes";
import { isAbortedRequest, isUnconfirmedOutcome, useRpcCaller } from "../context/WidgetRequestContext";
import type { CustomerPreOrder } from "../../../common/jsonRpcTypes";
import { isAuthenticationPayment } from "../../utils/paymentHelpers";
import { getFeatureNameForAction } from "../../utils/featureSentiment";
import { formatDate, formatTime } from "../../../utils/countryLocaleMap";
import { useWidgetLocale } from "../hooks/useWidgetLocale";
import { notifyBugsnag } from "../../bugsnag";
import analytics from "../../analytics";
import { loadFreedomPayScript, loadRecaptchaScript, ScriptAuthorisationError } from "../../../common/scriptSecurity";
import "../../styles/freedom-pay.scss";
import "../../styles/confirmation.scss";

interface FreedomPayWindow extends Window {
  ApplePaySession?: unknown;
  grecaptcha?: {
    execute: (key: string, options: { action: string }) => Promise<string>;
  };
  FreedomPay?: {
    Apm: {
      ConsumerAuth: {
        invoke: (options: { mandateChallenge: boolean }) => void;
      };
    };
  };
}

export function extractErrorMessage(message: { data?: { errors?: { message?: string }[] } }): string {
  if (
    message?.data &&
    Array.isArray(message.data.errors) &&
    message.data.errors.length > 0 &&
    message.data.errors[0].message
  ) {
    return message.data.errors[0].message;
  }
  return "An unknown error occurred.";
}

export function normaliseBoolean(val: unknown): boolean {
  if (typeof val === "string") return val.toLowerCase() === "true";
  return !!val;
}

interface FreedomPayProps {
  pendingBookingId?: string | false;
  pendingPreorderId?: string | false;
  bookingId?: string | false;
  action?: string;
  name?: string;
  email?: string;
  customerNotes?: string;
  amount?: number;
  onFinish?: (payment: any) => void;
  onPaymentSuccess?: (email?: string, amount?: number, paymentDetails?: unknown) => void;
  /** Called when finalising times out, so whether the guest was charged is unknown. */
  onPaymentUnconfirmed?: () => void;
  mode?: "full" | "successOnly" | "successWithDetails";
  bookingData?: {
    depositTotal: number;
    guestCount: number;
    time: string;
    date: string;
    bookingId: string;
    bookingRef: string;
    venue: {
      title: string;
      currency: string;
      country_code: string | null;
      collins_features?: Record<string, unknown> | string[];
    };
    paymentProcessor: string;
    depositTypePayment: string | boolean;
    /** The venue's deposit terms, repeated on the confirmation. */
    depositTerms?: string;
    user?: {
      user_id: string;
      user_name: string;
    };
    bookingDetails?: {
      first_name?: string | null;
      last_name?: string | null;
      email?: string | null;
      phone?: string | null;
      country_code?: string;
    };
  };
  showInfo?: boolean;
  vouchers?: Array<{ code: string; amountToRedeem: number }>;
}

const FreedomPay: React.FC<FreedomPayProps> = ({
  pendingBookingId = false,
  pendingPreorderId = false,
  bookingId = false,
  action,
  name,
  email,
  customerNotes,
  amount,
  onFinish,
  onPaymentSuccess,
  onPaymentUnconfirmed,
  bookingData,
  mode,
  showInfo = false,
  vouchers,
}) => {
  const call = useRpcCaller();
  const { t } = useTranslation();
  const timeLocale = useWidgetLocale(bookingData?.venue.country_code);
  const submittingRef = useRef(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [paymentStatus, setPaymentStatus] = useState<"idle" | "success" | "error" | "unconfirmed">("idle");
  const [formVisible, setFormVisible] = useState(true);
  const [countries, setCountries] = useState<any[]>([]);
  const [address, setAddress] = useState<any>({});
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("card");
  const [secure3d, setSecure3d] = useState(true);
  const [initialising, setInitialising] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preOrderData, setPreOrderData] = useState<CustomerPreOrder | null>(null);
  const paymentElementRef = useRef<HTMLDivElement>(null);
  const sessionKeyRef = useRef<string | false>(false);
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSafetyTimeout = React.useCallback(() => {
    if (safetyTimeoutRef.current !== null) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
  }, []);

  const [freedomPayUrl, setFreedomPayUrl] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState<string | null>(null);

  const handleMessage = React.useCallback(
    (e: MessageEvent) => {
      const message = e.data;
      const { data } = message;
      if (message.type === 1) {
        setErrorMessages((prev) => [...prev, extractErrorMessage(message)]);
      }
      if (message.type === 16 && message.data?.Payment) {
        const { ECIFlag, PAResStatus } = message.data.Payment.ExtendedData;
        if (["07", "00"].includes(ECIFlag) && ["N", "R"].includes(PAResStatus)) {
          setErrorMessages((prev) => [...prev, t("transaction_declined", "Transaction declined. Please try again.")]);
          setSecure3d(false);
        }
      }
      if (message.type === 16 && message.data?.CmpiLookup) {
        const { EciFlag, PAResStatus, Enrolled } = message.data.CmpiLookup;
        if (["07", "00"].includes(EciFlag) && Enrolled === "Y" && ["N", "R"].includes(PAResStatus)) {
          setErrorMessages((prev) => [...prev, t("transaction_declined", "Transaction declined. Please try again.")]);
          setSecure3d(false);
        }
      }
      if (message.type === 3) {
        if (secure3d === true) {
          submitPayment(data);
        }
      }
    },
    [secure3d, t],
  );

  useEffect(() => {
    Promise.all([
      // Fetch FreedomPay URL from backend
      call("/freedompay/url", {})
        .then((url) => {
          setFreedomPayUrl(url);
        })
        .catch((error) => {
          if (isAbortedRequest(error)) return;
          notifyBugsnag(new Error("Failed to fetch FreedomPay URL"), {
            component: "FreedomPay",
            route: "/freedompay/url",
            error,
          });
          setFreedomPayUrl(null);
        }),

      // Fetch captcha key from backend
      call("/captcha-key", {})
        .then((key) => {
          setCaptchaKey(key);
        })
        .catch(() => setCaptchaKey(null)),

      // Fetch countries through backend
      call("/freedompay/countries", {})
        .then((data) => setCountries(data ?? []))
        .catch((error) => {
          if (isAbortedRequest(error)) return;
          notifyBugsnag(new Error("Failed to fetch FreedomPay countries"), {
            component: "FreedomPay",
            route: "/freedompay/countries",
            error,
          });
          setCountries([]);
        }),
    ]).finally(() => setLoading(false));
  }, [call]);

  const fpWindow = window as FreedomPayWindow;
  const browserSupportsApplePay = !!fpWindow.ApplePaySession;
  const rawCf = bookingData?.venue?.collins_features;
  const cf = rawCf && !Array.isArray(rawCf) ? rawCf : undefined;
  const enableMobile = normaliseBoolean(cf?.fpay_mobile_payments);
  const configApplePay = normaliseBoolean(cf?.fpay_apple_pay);
  const configGooglePay = normaliseBoolean(cf?.fpay_google_pay);

  const showApplePay = enableMobile && configApplePay && browserSupportsApplePay;
  const showGooglePay = enableMobile && configGooglePay;

  useEffect(() => {
    // Load FreedomPay script once URL is available using secure script loader
    if (!freedomPayUrl) return;

    let mounted = true;

    loadFreedomPayScript(freedomPayUrl, {
      onError: (error) => {
        if (!mounted) return;
        if (error instanceof ScriptAuthorisationError) {
          notifyBugsnag(error, {
            component: "FreedomPay",
            securityEvent: "unauthorised_script_blocked",
            url: freedomPayUrl,
          });
          setErrorMessages((prev) => [
            ...prev,
            t("payment_unavailable", "Payment service is temporarily unavailable."),
          ]);
        } else {
          notifyBugsnag(error, {
            component: "FreedomPay",
            event: "script_load_failed",
            url: freedomPayUrl,
          });
        }
      },
    }).catch((error) => {
      // Error already handled in onError callback
      console.error("[FreedomPay] Script loading failed:", error);
    });

    // Add message listener only once
    window.addEventListener("message", handleMessage);
    return () => {
      mounted = false;
      window.removeEventListener("message", handleMessage);
      clearSafetyTimeout();
    };
  }, [clearSafetyTimeout, freedomPayUrl, handleMessage, t]);

  useEffect(() => {
    if (captchaKey) loadGoogleCaptcha();
  }, [captchaKey]);

  function loadGoogleCaptcha() {
    if (!captchaKey || typeof captchaKey !== "string" || !/^[a-zA-Z0-9_-]+$/.test(captchaKey)) {
      return;
    }

    // Load reCAPTCHA script using secure script loader
    loadRecaptchaScript(captchaKey, {
      onError: (error) => {
        if (error instanceof ScriptAuthorisationError) {
          notifyBugsnag(error, {
            component: "FreedomPay",
            securityEvent: "unauthorised_recaptcha_script_blocked",
          });
        } else {
          // Non-critical error - reCAPTCHA is optional for some flows
          console.warn("[FreedomPay] reCAPTCHA script loading failed:", error);
        }
      },
    }).catch((error) => {
      // Error already handled in onError callback
      console.warn("[FreedomPay] reCAPTCHA loading failed:", error);
    });
  }

  function getCaptchaToken() {
    return new Promise<string>((resolve, reject) => {
      const fpWin = window as FreedomPayWindow;
      if (!fpWin.grecaptcha) {
        reject(new Error(t("recaptcha_not_loaded", "Google reCAPTCHA not loaded")));
        return;
      }
      if (!captchaKey) {
        reject(new Error(t("recaptcha_key_missing", "reCAPTCHA key is not configured")));
        return;
      }

      fpWin.grecaptcha
        .execute(captchaKey, { action: "submit" })
        .then((token: string) => {
          if (!token || typeof token !== "string") {
            reject(new Error(t("invalid_captcha_token", "Invalid captcha token received")));
            return;
          }
          resolve(token);
        })
        .catch((error: unknown) => reject(error instanceof Error ? error : new Error(String(error))));
    });
  }

  // Convert camelCase form data to API's PascalCase keys
  function mapCustomerDetailsToApi(details: CustomerDetails) {
    return {
      BillingFirstName: details.billingFirstName,
      BillingLastName: details.billingLastName,
      Email: details.email,
      MobilePhone: details.mobilePhone,
      BillingAddress1: details.billingAddress1,
      BillingCity: details.billingCity,
      BillingPostalCode: details.billingPostalCode,
      BillingCountryCode: details.billingCountryCode,
    };
  }

  function handleCustomerDetailsSubmit(details: CustomerDetails) {
    const apiDetails = mapCustomerDetailsToApi(details);
    setAddress(apiDetails);
    setFormVisible(false);
    submittingRef.current = true;
    initialisePayment(apiDetails, "card");
  }

  function mountPaymentElement(iframe: string) {
    if (!paymentElementRef.current) {
      // Ref unavailable — clear initialising so the UI doesn't hang
      submittingRef.current = false;
      setInitialising(false);
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(iframe, "text/html");
    const iframeElement = doc.body.firstChild;

    if (iframeElement) {
      paymentElementRef.current.textContent = "";
      paymentElementRef.current.appendChild(iframeElement);
    }
    submittingRef.current = false;

    // Wait for the iframe to finish loading before hiding the spinner
    const iframeEl = paymentElementRef.current.querySelector("iframe");
    if (iframeEl) {
      const onLoad = () => {
        clearSafetyTimeout();
        setInitialising(false);
        iframeEl.removeEventListener("load", onLoad);
      };
      iframeEl.addEventListener("load", onLoad);
      // Safety timeout in case the load event never fires
      clearSafetyTimeout();
      safetyTimeoutRef.current = setTimeout(() => {
        safetyTimeoutRef.current = null;
        setInitialising(false);
      }, 15_000);
    } else {
      setInitialising(false);
    }
  }

  async function initialisePayment(address: any, paymentMethod: string) {
    submittingRef.current = true;
    // Cancel any pending safety timeout from a previous initialisation
    clearSafetyTimeout();
    setInitialising(true);
    try {
      const paymentFormData = await initialiseCheckout(address, paymentMethod);
      if (paymentFormData.error) {
        setErrorMessages((prev) => [...prev, paymentFormData.error]);
        submittingRef.current = false;
        setInitialising(false);
        if (paymentMethod === "card") {
          setFormVisible(true);
        }
        return;
      }
      // mountPaymentElement will clear initialising once the iframe loads
      mountPaymentElement(paymentFormData.iframe);
      sessionKeyRef.current = paymentFormData.sessionKey;
    } catch (error: any) {
      setErrorMessages((prev) => [...prev, error.message]);
      setInitialising(false);
      if (paymentMethod === "card") {
        setFormVisible(true);
      }
    }
  }

  async function initialiseCheckout(address: any, paymentMethod: string) {
    const body: any = {
      paymentMethod,
      address,
    };
    if (bookingId) body.booking_id = bookingId;
    if (pendingBookingId) body.pending_booking_id = pendingBookingId;
    if (action) body.action = action;

    if (name) body.name = name;
    if (email) body.email = email;
    if (customerNotes) body.customer_notes = customerNotes;

    // Add vouchers if provided
    if (vouchers && Array.isArray(vouchers) && vouchers.length > 0) {
      body.vouchers = vouchers;
    }

    if (captchaKey) {
      body.recaptchaToken = await getCaptchaToken();
    }

    try {
      const response = await call("/freedompay/initialise", body);
      return response;
    } catch (error) {
      notifyBugsnag(new Error("Failed to initialise FreedomPay checkout"), {
        component: "FreedomPay",
        route: "/freedompay/initialise",
        bookingId,
        pendingBookingId,
        action,
        error,
      });
      throw new Error(`Failed to initialise checkout: ${error}`);
    }
  }

  function submitPayment(data: any) {
    const paymentBookingId = typeof bookingId === "string" ? bookingId : undefined;
    // The journey this attempt belongs to: the pending booking on the initial /book
    // flow, where there is no booking id yet, and the booking itself everywhere else.
    const paymentJourneyId = typeof pendingBookingId === "string" ? pendingBookingId : paymentBookingId;

    // `payment_initiated` — the hosted payment form has validated the card and
    // returned its keys, so finalisation is about to begin and the outcome is not
    // yet known. A retry raises it again.
    void analytics.paymentInitiated({
      id: paymentBookingId ?? paymentJourneyId,
      journeyId: paymentJourneyId,
      value: amount,
      currency: bookingData?.venue?.currency,
      numPeople: bookingData?.guestCount,
    });

    finalise(data);
  }

  function buildFinaliseRequestBody(paymentMethod: any, currentSessionKey: string | false) {
    const body: any = {
      PaymentKeys: paymentMethod.paymentKeys,
      attributes: paymentMethod.attributes,
      paymentType: paymentMethod.paymentType,
      sessionKey: currentSessionKey,
    };
    if (bookingId) body.booking_id = bookingId;
    if (address) body.address = address;
    if (pendingBookingId) body.pending_booking_id = pendingBookingId;
    if (pendingPreorderId) body.pending_preorder_id = pendingPreorderId;
    if (action) body.action = action;
    return body;
  }

  async function handleSuccessfulPayment(responseBody: any) {
    setPaymentStatus("success");
    onFinish?.(responseBody.payment);
    onPaymentSuccess?.(email, amount, responseBody.payment);

    if (!bookingId || typeof bookingId !== "string") return;

    const clearsPreorder = action === "place-your-order" || action === "manage-your-order" || pendingPreorderId;
    if (clearsPreorder && (await clearPaidPreorder(bookingId)) === "aborted") return;

    await loadPaidPreorder(bookingId);
  }

  async function clearPaidPreorder(paidBookingId: string): Promise<"done" | "aborted"> {
    try {
      await call("/clearPreorder", { booking_id: paidBookingId });
    } catch (error) {
      if (isAbortedRequest(error)) return "aborted";
      console.error("Failed to clear preorder after payment:", error);
    }
    return "done";
  }

  async function loadPaidPreorder(paidBookingId: string) {
    try {
      const preOrder = await call("/getCustomerPreOrder", { booking_id: paidBookingId });
      if (preOrder) {
        setPreOrderData(preOrder);
      }
    } catch (error) {
      if (isAbortedRequest(error)) return;
      console.log("No pre-order data available");
    }
  }

  async function finalise(paymentMethod: any) {
    submittingRef.current = true;
    const body = buildFinaliseRequestBody(paymentMethod, sessionKeyRef.current);

    try {
      const responseBody = await call("/freedompay/finalise", body);
      if (!responseBody.successful || responseBody.successful === false) {
        if (responseBody.message === "216") {
          (window as FreedomPayWindow).FreedomPay?.Apm.ConsumerAuth.invoke({ mandateChallenge: true });
        } else {
          const error = responseBody.message ?? "An error occurred";
          setErrorMessages((prev) => [...prev, error]);
          setPaymentStatus("error");
          submittingRef.current = false;
        }
        return;
      }
      await handleSuccessfulPayment(responseBody);
    } catch (error) {
      notifyBugsnag(new Error("Failed to finalise FreedomPay checkout"), {
        component: "FreedomPay",
        route: "/freedompay/finalise",
        bookingId,
        pendingBookingId,
        pendingPreorderId,
        action,
        error,
      });
      const unconfirmed = isUnconfirmedOutcome(error);
      const message = unconfirmed
        ? t(
            "payment_timeout_message",
            "We are still confirming your payment. Please do not pay again as this could result in a duplicate charge. Check your email for a confirmation and if nothing arrives within 10 minutes, get in touch with the venue directly.",
          )
        : `Payment failed: ${error}`;
      setErrorMessages((prev) => [...prev, message]);
      // An unconfirmed outcome may still have gone through, so the payment form
      // is replaced by the timeout message rather than offered again.
      setPaymentStatus(unconfirmed ? "unconfirmed" : "error");
      if (unconfirmed) onPaymentUnconfirmed?.();
      else submittingRef.current = false;
    }
  }

  function selectPaymentMethod(method: string) {
    setSelectedPaymentMethod(method);
    setFormVisible(method === "card");
    if (method !== "card") {
      initialisePayment({}, method);
    }
  }

  const customerFormInitialValues = useMemo(() => {
    const values: Partial<CustomerDetails> = {};
    const details = bookingData?.bookingDetails;
    if (name) {
      const parts = name.trim().split(/\s+/);
      values.billingFirstName = parts[0] ?? "";
      values.billingLastName = parts.length > 1 ? (parts[parts.length - 1] ?? "") : "";
    } else if (details?.first_name || details?.last_name) {
      values.billingFirstName = details.first_name ?? "";
      values.billingLastName = details.last_name ?? "";
    }
    if (email) {
      values.email = email;
    } else if (details?.email) {
      values.email = details.email;
    }
    if (details?.phone) {
      values.mobilePhone = details.phone;
    }
    const rawCountryCode = bookingData?.venue?.country_code ?? details?.country_code;
    if (rawCountryCode) {
      const match = countries.find(
        (c: { abbreviation?: string }) => c.abbreviation?.toUpperCase() === rawCountryCode.toUpperCase(),
      );
      values.billingCountryCode = match?.code ?? rawCountryCode;
    }
    return values;
  }, [name, email, bookingData?.bookingDetails, bookingData?.venue?.country_code, countries]);

  return (
    <div>
      {paymentStatus === "success" && bookingData && (
        <PaymentConfirmationLayout
          venueName={bookingData.venue.title}
          bookingRef={bookingData.bookingRef}
          depositTotal={amount ?? bookingData.depositTotal ?? 0}
          isAuthentication={isAuthenticationPayment(bookingData.depositTypePayment)}
          partySize={bookingData.guestCount}
          date={
            bookingData.date && typeof bookingData.date === "string"
              ? formatDate(bookingData.date, bookingData.venue.country_code ?? "GB", "short")
              : ""
          }
          time={
            bookingData.time && bookingData.date && typeof bookingData.date === "string"
              ? formatTime(bookingData.time, bookingData.date, bookingData.venue.country_code ?? "GB", timeLocale)
              : bookingData.time
          }
          location={bookingData.venue.title}
          preOrderItems={preOrderData?.items}
          currency={bookingData.venue.currency}
          countryCode={bookingData.venue.country_code}
          mode={mode}
          email={email}
          featureName={getFeatureNameForAction(action ?? "secure-your-booking")}
          userId={bookingData.user?.user_id ?? null}
          customerName={bookingData.user?.user_name ?? null}
          depositTerms={bookingData.depositTerms}
        />
      )}

      {paymentStatus !== "success" && errorMessages.length > 0 && <PaymentError errorMessages={errorMessages} />}

      {paymentStatus !== "success" && paymentStatus !== "unconfirmed" && loading && (
        <div
          className="gcw:flex gcw:justify-center gcw:py-bs-3"
          aria-live="polite"
          data-testid="freedompay-setup-loading"
        >
          <div className="loading-spinner" />
          <span className="gcw:sr-only">{t("loading")}</span>
        </div>
      )}

      {paymentStatus !== "success" && paymentStatus !== "unconfirmed" && !loading && (
        <div>
          {showInfo && <Info bookingData={bookingData} amount={amount} />}
          <div>
            <p className="gcw:text-body gcw:font-bold">{t("payment_method", "Payment method")}</p>
            <PaymentMethod
              selectedPaymentMethod={selectedPaymentMethod}
              onSelect={selectPaymentMethod}
              showApplePay={showApplePay}
              showGooglePay={showGooglePay}
            />
          </div>
          {formVisible ? (
            <CustomerDetailsForm
              countries={countries}
              onSubmit={handleCustomerDetailsSubmit}
              errorMessages={errorMessages}
              initialValues={customerFormInitialValues}
            />
          ) : (
            <div id="gc-fp-payment-form">
              <div className="gcw:mb-bs-3" id="payment-form">
                {initialising && (
                  <div
                    className="gcw:flex gcw:justify-center gcw:py-bs-3"
                    aria-live="polite"
                    data-testid="freedompay-loading"
                  >
                    <div className="loading-spinner" />
                    <span className="gcw:sr-only">{t("loading")}</span>
                  </div>
                )}
                <div id="gc-fp-payment-element" data-gc-fp-state={initialising ? "loading" : "ready"}>
                  <div
                    id="payment-element"
                    ref={paymentElementRef}
                    style={initialising ? { visibility: "hidden", height: 0, overflow: "hidden" } : undefined}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FreedomPay;
